import React, { memo, useRef } from 'react';
import { styled } from '@compiled/react';
import Icon from '@atlaskit/icon';
import { colors } from '@atlaskit/theme';
import { token } from '@atlaskit/tokens';
import { gridSize } from '@atlassian/jira-common-styles/src/main.tsx';
import { isVisualRefreshEnabled } from '@atlassian/jira-visual-refresh-rollout/src/feature-switch';
import { DividerStatistics } from './divider-statistics';
import { GrabberIcon } from './grabber-icon';
import { useDraggableDivider } from './use-draggable-divider';

type Props = {
	id: string;
};

const GRABBER_SELECTOR = 'software-backlog-divider-grabber';
const GRABBER_LINE_SELECTOR = 'software-backlog-divider-grabber-line';

const DividerContents = memo(({ id }: Props) => {
	const content = (
		<>
			<GrabberContainer>
				<GrabberLine data-component-selector={GRABBER_LINE_SELECTOR} />
			</GrabberContainer>
			<Grabber data-component-selector={GRABBER_SELECTOR}>
				<Icon
					glyph={GrabberIcon}
					size="small"
					primaryColor={token('color.icon.subtle', colors.N90)}
					label=""
				/>
			</Grabber>
			<GrabberContainer>
				<GrabberLine data-component-selector={GRABBER_LINE_SELECTOR} />
				<DividerStatistics dividerCardListId={id} />
			</GrabberContainer>
		</>
	);

	const containerRef = useRef<HTMLDivElement | null>(null);
	const { dragPreview } = useDraggableDivider({
		elementRef: containerRef,
		cardListId: id,
	});

	return (
		<>
			<Container
				ref={containerRef}
				data-testid="software-backlog.card-list.divider.container"
				aria-hidden="true"
				visualRefresh={isVisualRefreshEnabled()}
			>
				{content}
			</Container>
			{dragPreview}
		</>
	);
});

export default function Divider(props: Props) {
	const content = <DividerContents id={props.id} />;

	return <>{content}</>;
}

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const GrabberLine = styled.div({
	display: 'inline-flex',
	flexGrow: 1,
	height: 0,
	opacity: 0,

	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
	borderBottom: `2px solid ${token('color.border', colors.N90)}`,
	transition: 'opacity 0.2s ease-in-out',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const Grabber = styled.span({
	display: 'inline-flex',

	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
	height: `${gridSize * 2}px`,

	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
	width: `${gridSize * 2}px`,
	opacity: 0.8,
	transition: 'opacity 0.2s ease-in-out',
	cursor: 'row-resize',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const GrabberContainer = styled.div({
	display: 'inline-flex',
	flexBasis: 0,
	flexGrow: 1,
	alignItems: 'center',
	gap: token('space.100', '8px'),
	backgroundColor: 'transparent',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const Container = styled.div<{ visualRefresh: boolean }>(
	{
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
		height: `${gridSize * 4}px`,
		display: 'flex',
		flexDirection: 'row',
		alignItems: 'center',
		justifyContent: 'center',
		gap: token('space.100', '8px'),
		marginBottom: token('space.150', '0.75rem'),
		flexGrow: 1,
		'&:hover, &:active': {
			// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-values, @atlaskit/ui-styling-standard/no-nested-selectors -- Ignored via go/DSP-18766
			[`[data-component-selector="${GRABBER_SELECTOR}"], [data-component-selector="${GRABBER_LINE_SELECTOR}"]`]:
				{
					opacity: 1,
				},
		},
	},
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles
	({ visualRefresh }) => !visualRefresh && { padding: `0 ${token('space.200', '16px')}` },
);
